// UnitApplicationPermissions
// ***********************************************************************************************************************
import { Alert, Box, FormControlLabel, Grid, Radio, Typography } from "@mui/material";
import * as React from "react";
import { Dropdown, IDropdownAction } from "ui/Scripts/source/components/framework/Dropdown/Dropdown";
import { Base } from "ui/Scripts/source/framework/base";
import { EnumHelper, GroupType, UserGroupType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IUnitNodePermission } from "../../../../models/unit/unitNodePermission";
import { IUnitPermissionUserGroup, UnitPermissionUserGroup } from "../../../../models/userGroup/unitPermissionUserGroup";
import { FolderNode, IFolderNode } from "ui/Scripts/source/models/document/folderNode";
import { IFolderNodePermission } from "ui/Scripts/source/models/document/folderNodePermission";
import { FolderPermissionTree } from "../FolderPermissionTree";
import { GridRow } from "source/components/framework/Grid/GridRow";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListIconButtonsPermission } from "source/components/framework/List/ListIconButtons/ListIconButtonsPermission";
import { ListHeaderLine } from "source/components/framework/List/ListLine";
import { FolderNodePermissionDialog } from "../FolderNodePermissionDialog";
import { AppUtils } from "source/framework/appUtils";

export interface IFolderPermissionsProp {
    canUseAllCompanyGroup: boolean;
    companyId: string;
    folderId: string;
    disabled?: boolean;

    userGroupMode: boolean;
    folderNode: IFolderNode;
    groups: IUnitPermissionUserGroup[];

    onChangeSaveFolderNode: (saveFolderNode: IFolderNode) => void;
}

interface IFolderPermissionsState {
    allUsersGroup: boolean;
    userGroupType: UserGroupType;
    allGroups: IUnitPermissionUserGroup[];
    groups: IUnitPermissionUserGroup[];
    userGroupDropDownItems: IDropdownAction[];
    group: IUnitPermissionUserGroup;
    copiedPermissions: { [id: string]: IFolderNodePermission; };
    permissionsCopied: boolean;
    folderNode: IFolderNode;
    displayFolderNode: IFolderNode;
    editFolderNode: IFolderNode;
    selectedFolderId: string;
    showFolderPermissionDialog: boolean;
    folderPermissionDialogGroup: IUnitPermissionUserGroup;
}

export class FolderPermissions extends React.Component<IFolderPermissionsProp, IFolderPermissionsState> {
    setFixedGroupNames = (groups: IUnitPermissionUserGroup[]) => {
        const fixedGroups = groups.filter(i => !!i.code && Base.equalInt(i.type, UserGroupType.Fixed));
        for (const group of fixedGroups) {
            group.groupNames = groups.filter(i => Base.equalInt(i.type, UserGroupType.Normal) && group.groupCodes.indexOf(i.code) > -1).map(i => i.name).sort();
        }
    };

    getUserGroupDropDownItems = (userGroups: IUnitPermissionUserGroup[], selectedUserGroup: IUnitPermissionUserGroup): IDropdownAction[] => {
        return userGroups.map((userGroup) => {
            const groupNames = Base.getStringWithSeparators(userGroup.groupNames, ", ");
            return {
                title: <Box display="flex" alignItems="baseline"><Typography>{userGroup.name}</Typography>{groupNames && <Typography variant="caption" color="text.secondary">&nbsp;({groupNames})</Typography>}</Box>,
                textTitle: userGroup.name + (groupNames ? " (" + groupNames + ")" : ""),
                selected: selectedUserGroup.code === userGroup.code,
                onClick: () => this.userGroupChange(userGroup.code)
            };
        });
    };

    getUserGroupDropDownItemsAll = (userGroups: IUnitPermissionUserGroup[], selectedUserGroup: IUnitPermissionUserGroup, allUsersGroup: boolean, userGroupType: UserGroupType): IDropdownAction[] => {
        const nonQrCodeUserGroups = userGroups.filter(i => !i.qrCode);
        const qrCodeUserGroups = userGroups.filter(i => i.qrCode);
        const result = this.getUserGroupDropDownItems(nonQrCodeUserGroups, selectedUserGroup);
        if (nonQrCodeUserGroups.length > 0 && qrCodeUserGroups.length > 0 && !allUsersGroup) {
            const dividerTitle = userGroupType === UserGroupType.Normal
                ? Translations.QrCodeUserGroups
                : Translations.QrCodeUsers;
            result.push({
                title: dividerTitle,
                textTitle: dividerTitle,
                divider: true,
                onClick: null
            });
        }
        return result.concat(this.getUserGroupDropDownItems(qrCodeUserGroups, selectedUserGroup));
    };

    static getUserGroups = (groups: IUnitPermissionUserGroup[], allUsersGroup: boolean, userGroupType: UserGroupType): IUnitPermissionUserGroup[] => {
        return allUsersGroup
            ? groups.filter(i => !i.code && Base.equalInt(i.type, UserGroupType.Normal))
            : groups.filter(i => !!i.code && Base.equalInt(i.type, userGroupType));
    };

    getUserGroupsNew = (groups: IUnitPermissionUserGroup[], allUsersGroup: boolean, userGroupType: UserGroupType): {
        userGroups: IUnitPermissionUserGroup[];
        userGroup: IUnitPermissionUserGroup;
        userGroupDropDownItems: IDropdownAction[];
     } => {
        const userGroups = FolderPermissions.getUserGroups(groups, allUsersGroup, userGroupType);
        const userGroup = userGroups.length > 0 ? userGroups[0] : new UnitPermissionUserGroup();
        return {
            userGroups,
            userGroup,
            userGroupDropDownItems: this.getUserGroupDropDownItemsAll(userGroups, userGroup, allUsersGroup, userGroupType)
        };
    };

    setEffectivePermissions = (folderNode: IFolderNode, group: IUnitPermissionUserGroup) => {
        const userGroupMode = this.props.userGroupMode;
        const companyGroup = userGroupMode ? null : group;
        const userGroup = !userGroupMode ? null : group;
        folderNode.setEffectivePermissions(companyGroup, userGroup, null);
    };

    constructor(props: IFolderPermissionsProp) {
        super(props);
        const allGroups = props.groups.map(i => { return new UnitPermissionUserGroup(i); });
        this.setFixedGroupNames(allGroups);
        const userGroups = this.getUserGroupsNew(allGroups, props.userGroupMode || props.canUseAllCompanyGroup, props.userGroupMode || props.canUseAllCompanyGroup ? UserGroupType.Normal : UserGroupType.Fixed);
        const folderNode = new FolderNode(props.folderNode);
        this.setEffectivePermissions(folderNode, userGroups.userGroup);
        const displayFolderNode = folderNode.getFolderNodeById(props.folderId);
        displayFolderNode.expanded = true;
        this.state = {
            folderNode,
            displayFolderNode,
            editFolderNode: null,
            allUsersGroup: true,
            userGroupType: UserGroupType.Normal,
            allGroups,
            groups: userGroups.userGroups,
            group: userGroups.userGroup,
            userGroupDropDownItems: userGroups.userGroupDropDownItems,
            permissionsCopied: false,
            copiedPermissions: {},
            selectedFolderId: props.folderId,
            showFolderPermissionDialog: false,
            folderPermissionDialogGroup: null
        };
    }

    getUpdatedFolderNode = (group: IUnitPermissionUserGroup): IFolderNode => {
        if (!group) return this.state.folderNode;
        return AppUtils.runLongTask(() => {
            const folderNode = new FolderNode(this.state.folderNode);
            this.setEffectivePermissions(folderNode, group);
            return folderNode;
        });
    };

    userGroupChange = (groupCode: string) => {
        const props = this.props;
        const group = this.state.groups.find(i => i.code === groupCode);
        if (!group) return;
        const folderNode = this.getUpdatedFolderNode(group);
        this.setState({
            group,
            userGroupDropDownItems: this.getUserGroupDropDownItemsAll(this.state.groups, group, this.state.allUsersGroup, this.state.userGroupType),
            folderNode,
            displayFolderNode: folderNode.getFolderNodeById(props.folderId)
        });
    };

    handleChangeUserGroupTypeAll = (checked: boolean) => {
        if (!checked) return;
        const props = this.props;
        const userGroups = this.getUserGroupsNew(this.state.groups, true, UserGroupType.Normal);
        const folderNode = this.getUpdatedFolderNode(userGroups.userGroup);
        this.setState({
            allUsersGroup: true,
            groups: userGroups.userGroups,
            group: userGroups.userGroup,
            userGroupDropDownItems: userGroups.userGroupDropDownItems,
            folderNode,
            displayFolderNode: folderNode.getFolderNodeById(props.folderId)
        });
    };

    handleChangeUserGroupType = (checked: boolean, userGroupType: UserGroupType) => {
        if (!checked) return;
        const props = this.props;
        const userGroups = this.getUserGroupsNew(this.state.allGroups, false, userGroupType);
        const folderNode = this.getUpdatedFolderNode(userGroups.userGroup);
        this.setState({
            allUsersGroup: false,
            userGroupType,
            groups: userGroups.userGroups,
            group: userGroups.userGroup,
            userGroupDropDownItems: userGroups.userGroupDropDownItems,
            folderNode,
            displayFolderNode: folderNode.getFolderNodeById(props.folderId)
        });
    };
    
    editPermission = (id: string) => {
        const state = this.state;
        if (!id) return;
        const group = state.groups.find(i => i.code === state.group.code);
        if (!group) return;
        const editFolderNode = state.folderNode.getFolderNodeById(id);
        if (!editFolderNode) return;
        this.setState({
            showFolderPermissionDialog: true,
            editFolderNode,
            folderPermissionDialogGroup: group
        });
    };

    handleFolderNodeClick = (id: string) => {
        if (!id) return;
        const folderNode = new FolderNode(this.state.folderNode);
        const node = folderNode.getFolderNodeById(id);
        if (!node) return;
        if (node.parent) {
            node.expanded = !node.expanded;
        }
        this.setState({
            selectedFolderId: id,
            folderNode,
            displayFolderNode: folderNode.getFolderNodeById(this.props.folderId)
        });
    };

    handleEditPermission = (id: string) => {
        this.editPermission(id);
    };

    copyPermissionToSiblings = (folderNode: IFolderNode, companyGroup: IUnitPermissionUserGroup, userGroup: IUnitPermissionUserGroup, permission: number) => {
        if (!folderNode.hasSiblings()) return;
        const siblings = folderNode.getSiblings();
        for (let i = 0; i < siblings.length; i++) {
            siblings[i].setGroupPermission(companyGroup, userGroup, permission, false);
        }
    };

    handleUnitGroupPermissionDialogOk = (permission: number, clearSubFolderPermissions: boolean, copyPermissionToAllSameLevelFolders: boolean) => {
        const state = this.state;
        const props = this.props;
        AppUtils.runLongTask(() => {
            const folderNode = new FolderNode(state.folderNode);
            const editFolderNode = folderNode.getFolderNodeById(state.editFolderNode.id);
            if (!editFolderNode) return;
            const companyGroup = props.userGroupMode ? null : state.folderPermissionDialogGroup;
            const userGroup = !props.userGroupMode ? null : state.folderPermissionDialogGroup;
            editFolderNode.setGroupPermission(companyGroup, userGroup, permission, clearSubFolderPermissions);
            let updateFolderNode = editFolderNode;
            if (copyPermissionToAllSameLevelFolders) {
                this.copyPermissionToSiblings(editFolderNode, companyGroup, userGroup, permission);
                updateFolderNode = editFolderNode.parent;
            }
            updateFolderNode.setEffectivePermissions(companyGroup, userGroup);
            const displayFolderNode = folderNode.getFolderNodeById(props.folderId);
            this.setState({
                showFolderPermissionDialog: false,
                folderNode,
                displayFolderNode
            });
            props.onChangeSaveFolderNode(displayFolderNode);
            return folderNode;
        });
    };

    handleUnitGroupPermissionDialogCancel = () => {
        this.setState({
            showFolderPermissionDialog: false
        });
    };

    handleUnitGroupPermissionDialogRemove = () => {
        const props = this.props;
        const state = this.state;
        const folderNode = new FolderNode(state.folderNode);
        const editFolderNode = folderNode.getFolderNodeById(state.selectedFolderId);
        if (!editFolderNode) return;
        editFolderNode.removeGroupPermission(props.userGroupMode ? GroupType.UserGroup : GroupType.CompanyGroup, state.folderPermissionDialogGroup.code);
        const companyGroup = props.userGroupMode ? null : state.folderPermissionDialogGroup;
        const userGroup = !props.userGroupMode ? null : state.folderPermissionDialogGroup;
        editFolderNode.setEffectivePermissions(companyGroup, userGroup);
        const displayFolderNode = folderNode.getFolderNodeById(props.folderId);
        this.setState({
            showFolderPermissionDialog: false,
            folderNode,
            displayFolderNode,
        });
        props.onChangeSaveFolderNode(displayFolderNode);
    };

    handleCopyPermissions = () => {
        const props = this.props;
        const state = this.state;
        const displayFolderNode = state.displayFolderNode;
        if (!displayFolderNode) return;
        const copiedPermissions: { [id: string]: IUnitNodePermission; } = {};
        const companyGroupCode = props.userGroupMode ? null : state.group.code;
        const userGroupCode = !props.userGroupMode ? null : state.group.code;
        this.setState({
            copiedPermissions: displayFolderNode.getNodePermissions(companyGroupCode, userGroupCode, copiedPermissions),
            permissionsCopied: true
        });
    };

    handlePastePermissions = () => {
        const obj = this;
        const props = this.props;
        const state = this.state;
        if (!state.permissionsCopied) return;
        const group = state.groups.find(i => i.code === state.group.code);
        if (!group) return;
        const companyGroup = props.userGroupMode ? null : group;
        const userGroup = !props.userGroupMode ? null : group;
        return AppUtils.runLongTask(() => {
            const folderNode = new FolderNode(this.state.folderNode);
            const displayFolderNode = folderNode.getFolderNodeById(props.folderId);
            if (!displayFolderNode) return;
            displayFolderNode.setGroupPermissions(companyGroup, userGroup, this.state.copiedPermissions);
            displayFolderNode.setEffectivePermissions(companyGroup, userGroup);
            obj.setState({
                folderNode,
                displayFolderNode
            });
            props.onChangeSaveFolderNode(displayFolderNode);
            return folderNode;
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <Box sx={{ margin: "-1rem 0 0" }}>
                    {props.userGroupMode &&
                        <Alert severity="info" sx={{ marginTop: ".5rem", marginBottom: "-.5rem" }}>{Translations.DocumentModuleInfoFolderPermissions}</Alert>
                    }
                    {(props.userGroupMode || props.canUseAllCompanyGroup) &&
                        <GridRow sx={{ marginTop: "0" }}>
                            <Grid item sm={4}>
                                <FormControlLabel
                                    label={props.userGroupMode ? Translations.UserGroupAll : Translations.CompanyGroupAll}
                                    control={
                                        <Radio
                                            checked={state.allUsersGroup}
                                            onChange={(_e, checked: boolean) => this.handleChangeUserGroupTypeAll(checked)}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item sm={4}>
                                <FormControlLabel
                                    label={props.userGroupMode ? Translations.UserGroups : Translations.CompanyGroups}
                                    control={
                                        <Radio
                                            checked={!state.allUsersGroup && EnumHelper.isEqual(state.userGroupType, UserGroupType.Normal)}
                                            onChange={(_e, checked: boolean) => this.handleChangeUserGroupType(checked, UserGroupType.Normal)}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item sm={4}>
                                <FormControlLabel
                                    label={props.userGroupMode ? Translations.Users : Translations.Companies}
                                    control={
                                        <Radio
                                            checked={!state.allUsersGroup && EnumHelper.isEqual(state.userGroupType, UserGroupType.Fixed)}
                                            onChange={(_e, checked: boolean) => this.handleChangeUserGroupType(checked, UserGroupType.Fixed)}
                                        />
                                    }
                                />
                            </Grid>
                        </GridRow>
                    }
                    <ActionContainer
                        leftItems={
                            <Dropdown
                                selectedTitle={state.group.name}
                                selectMode={true}
                                actions={state.userGroupDropDownItems}
                            />
                        }
                        rightItems={
                            <ListIconButtonsPermission
                                editTooltip={Translations.EditPermission}
                                copyTooltip={Translations.CopyGroupPermissions}
                                pasteTooltip={Translations.PasteGroupPermissions}
                                editDisabled={props.disabled}
                                copyDisabled={props.disabled}
                                pasteDisabled={props.disabled || !state.permissionsCopied}
                                onEdit={() => this.handleEditPermission(state.selectedFolderId)}
                                onCopy={this.handleCopyPermissions}
                                onPaste={this.handlePastePermissions}
                            />
                        }
                    />
                </Box>
                <Box sx={{ marginBottom: ".5rem" }}>
                    <ListHeaderLine
                        columns={[
                            { size: 5, column: "name", content: Translations.Folder },
                            { size: 7, column: "code", content: Translations.Permission }
                        ]}
                    />
                    <FolderPermissionTree
                        folderNode={state.displayFolderNode}
                        selectedId={state.selectedFolderId}
                        isFirstNode={true}
                        height={props.userGroupMode ? "calc(100vh - 490px)" : "calc(100vh - 422px)"}
                        onClick={this.handleFolderNodeClick}
                        onOpen={this.handleEditPermission}
                    />
                </Box>
                {state.showFolderPermissionDialog &&
                    <FolderNodePermissionDialog
                        folderNode={state.editFolderNode}
                        companyGroup={props.userGroupMode ? null : state.folderPermissionDialogGroup}
                        userGroup={!props.userGroupMode ? null : state.folderPermissionDialogGroup}
                        onOk={this.handleUnitGroupPermissionDialogOk}
                        onRemove={this.handleUnitGroupPermissionDialogRemove}
                        onCancel={this.handleUnitGroupPermissionDialogCancel}
                    />
                }
            </>
        );
    }
}